import { useState, useEffect, useMemo } from 'react';
import * as THREE from 'three';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import Model3DAnalyzer from '@/components/calculator/Model3DAnalyzer';
import Model3DViewer from '@/components/calculator/Model3DViewer';
import PrintSummaryModal from '@/components/calculator/PrintSummaryModal';
import {
  Currency,
  FilamentItem,
  CalculationProfile,
  Model3DStats,
} from '@/components/calculator/types';
import {
  CURRENCIES,
  PRINTER_PRESETS,
  SAVED_FILAMENTS,
  DEFAULT_PROFILE,
} from '@/components/calculator/constants';
import {
  Calculator,
  Zap,
  Clock,
  Sparkles,
  Save,
  Plus,
  Trash2,
  Share2,
  RotateCcw,
  Percent,
  Sliders,
  Package,
  Wrench,
  Brush,
  Paintbrush,
  Copy,
} from 'lucide-react';
import { toast } from 'sonner';

const STORAGE_PROFILES_KEY = 'mila_calculator_saved_profiles';

export default function CostCalculatorPage() {
  // Navigation / active tab for 3D section
  const [active3DTab, setActive3DTab] = useState<'analyzer' | 'viewer'>('analyzer');

  // 3D model state
  const [loadedObject3D, setLoadedObject3D] = useState<THREE.Object3D | null>(null);
  const [modelStats, setModelStats] = useState<Model3DStats | null>(null);

  // Currency
  const [currency, setCurrency] = useState<Currency>('ARS');

  // Fixed expenses
  const [kwhPrice, setKwhPrice] = useState<number>(DEFAULT_PROFILE.kwhPrice);
  const [selectedPrinterId, setSelectedPrinterId] = useState<string>(DEFAULT_PROFILE.selectedPrinterId);
  const [powerWatts, setPowerWatts] = useState<number>(DEFAULT_PROFILE.powerWatts);
  const [lifespanHours, setLifespanHours] = useState<number>(DEFAULT_PROFILE.lifespanHours);
  const [replacementCost, setReplacementCost] = useState<number>(DEFAULT_PROFILE.replacementCost);
  const [errorMarginPercent, setErrorMarginPercent] = useState<number>(DEFAULT_PROFILE.errorMarginPercent);

  // Piece & Filaments
  const [filaments, setFilaments] = useState<FilamentItem[]>(DEFAULT_PROFILE.filaments);
  const [printHours, setPrintHours] = useState<number>(DEFAULT_PROFILE.printHours);
  const [printMinutes, setPrintMinutes] = useState<number>(DEFAULT_PROFILE.printMinutes);

  // Extras
  const [suppliesCost, setSuppliesCost] = useState<number>(DEFAULT_PROFILE.suppliesCost);

  // Design
  const [designMode, setDesignMode] = useState<'fixed' | 'hourly'>('fixed');
  const [designFixedCost, setDesignFixedCost] = useState<number>(0);
  const [designHourlyRate, setDesignHourlyRate] = useState<number>(8500);
  const [designHours, setDesignHours] = useState<number>(0);

  // Post-processing
  const [postProcessMode, setPostProcessMode] = useState<'fixed' | 'hourly'>('fixed');
  const [postProcessFixedCost, setPostProcessFixedCost] = useState<number>(0);
  const [postProcessHourlyRate, setPostProcessHourlyRate] = useState<number>(6000);
  const [postProcessHours, setPostProcessHours] = useState<number>(0);

  // Profit
  const [profitMode, setProfitMode] = useState<'multiplier' | 'percentage'>('multiplier');
  const [profitMultiplier, setProfitMultiplier] = useState<number>(4); // Minorista (x4)
  const [profitPercentage, setProfitPercentage] = useState<number>(100);

  // External commission
  const [externalCommissionPercent, setExternalCommissionPercent] = useState<number>(18);

  // Profiles management
  const [savedProfiles, setSavedProfiles] = useState<CalculationProfile[]>([]);
  const [currentProfileName, setCurrentProfileName] = useState<string>('Mi Perfil Actual');
  const [selectedSavedProfileId, setSelectedSavedProfileId] = useState<string>('');

  // Quote modal
  const [isQuoteModalOpen, setIsQuoteModalOpen] = useState(false);

  // Load saved profiles from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_PROFILES_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed) && parsed.length > 0) {
          setSavedProfiles(parsed);
        }
      }
    } catch (e) {
      console.error('Error loading profiles from localStorage', e);
    }
  }, []);

  // When currency changes, update default kWh price if it was default
  const handleCurrencyChange = (newCurr: Currency) => {
    setCurrency(newCurr);
    const currConf = CURRENCIES.find((c) => c.code === newCurr);
    if (currConf) {
      setKwhPrice(currConf.defaultKwhPrice);
    }
  };

  // When printer preset changes
  const handlePrinterChange = (printerId: string) => {
    setSelectedPrinterId(printerId);
    const preset = PRINTER_PRESETS.find((p) => p.id === printerId);
    if (preset && preset.id !== 'custom') {
      setPowerWatts(preset.powerWatts);
      setLifespanHours(preset.lifespanHours);
      setReplacementCost(preset.replacementCost);
    }
  };

  // Filaments handling
  const handleAddFilament = () => {
    const nextNum = filaments.length + 1;
    const newFil: FilamentItem = {
      id: `fil-${Date.now()}`,
      name: `Filamento ${nextNum} — PLA+ (FILALAB)`,
      material: 'PLA+',
      colorName: 'Negro',
      colorHex: '#1a1a1a',
      pricePerKg: filaments[0]?.pricePerKg || 19500,
      weightGrams: 0,
    };
    setFilaments([...filaments, newFil]);
  };

  const handleRemoveFilament = (id: string) => {
    if (filaments.length <= 1) {
      toast.info('Debe haber al menos un filamento');
      return;
    }
    setFilaments(filaments.filter((f) => f.id !== id));
  };

  const handleUpdateFilament = (id: string, updates: Partial<FilamentItem>) => {
    setFilaments(filaments.map((f) => (f.id === id ? { ...f, ...updates } : f)));
  };

  const handleSelectSavedFilament = (id: string, presetIndex: number) => {
    const preset = SAVED_FILAMENTS[presetIndex];
    if (preset) {
      handleUpdateFilament(id, {
        name: preset.name,
        material: preset.material,
        colorName: preset.colorName,
        colorHex: preset.colorHex,
        pricePerKg: preset.pricePerKg,
      });
      toast.success(`Filamento "${preset.name}" aplicado.`);
    }
  };

  // Calculations
  const calculations = useMemo(() => {
    // Total print hours
    const totalHours = (Number(printHours) || 0) + (Number(printMinutes) || 0) / 60;

    // 1. Material cost
    const materialCost = filaments.reduce((acc, f) => {
      const kgPrice = Number(f.pricePerKg) || 0;
      const grams = Number(f.weightGrams) || 0;
      return acc + (kgPrice / 1000) * grams;
    }, 0);

    const totalGrams = filaments.reduce((acc, f) => acc + (Number(f.weightGrams) || 0), 0);

    // 2. Electricity cost: totalHours * (watts / 1000) * kwhPrice
    const electricityCost = totalHours * ((Number(powerWatts) || 0) / 1000) * (Number(kwhPrice) || 0);

    // 3. Wear & maintenance cost: (replacementCost / lifespanHours) * totalHours
    const wearPerHour = lifespanHours > 0 ? (Number(replacementCost) || 0) / (Number(lifespanHours) || 1) : 0;
    const wearCost = wearPerHour * totalHours;

    // Base costs
    const baseCosts = materialCost + electricityCost + wearCost;

    // Error margin cost: baseCosts * (errorMarginPercent / 100)
    const errorMarginCost = baseCosts * ((Number(errorMarginPercent) || 0) / 100);

    // Production cost: baseCosts + errorMarginCost
    const productionCost = baseCosts + errorMarginCost;

    // Fixed supplies
    const supplies = Number(suppliesCost) || 0;

    // Design cost
    const designCost =
      designMode === 'fixed'
        ? Number(designFixedCost) || 0
        : (Number(designHourlyRate) || 0) * (Number(designHours) || 0);

    // Post-processing cost
    const postProcessCost =
      postProcessMode === 'fixed'
        ? Number(postProcessFixedCost) || 0
        : (Number(postProcessHourlyRate) || 0) * (Number(postProcessHours) || 0);

    // Total costs
    const totalCosts = productionCost + supplies + designCost + postProcessCost;

    // Profit & final price
    let totalPrice = 0;
    if (profitMode === 'multiplier') {
      totalPrice = totalCosts * (Number(profitMultiplier) || 1);
    } else {
      totalPrice = totalCosts * (1 + (Number(profitPercentage) || 0) / 100);
    }

    // Commission price (+X%)
    const commissionPercent = Number(externalCommissionPercent) || 0;
    const priceWithCommission = totalPrice * (1 + commissionPercent / 100);

    return {
      totalHours,
      totalGrams,
      materialCost,
      electricityCost,
      wearCost,
      baseCosts,
      errorMarginCost,
      productionCost,
      supplies,
      designCost,
      postProcessCost,
      totalCosts,
      totalPrice,
      priceWithCommission,
      commissionPercent,
    };
  }, [
    printHours,
    printMinutes,
    filaments,
    powerWatts,
    kwhPrice,
    replacementCost,
    lifespanHours,
    errorMarginPercent,
    suppliesCost,
    designMode,
    designFixedCost,
    designHourlyRate,
    designHours,
    postProcessMode,
    postProcessFixedCost,
    postProcessHourlyRate,
    postProcessHours,
    profitMode,
    profitMultiplier,
    profitPercentage,
    externalCommissionPercent,
  ]);

  // Profile Save / Overwrite / Load
  const handleSaveProfile = () => {
    const profileName = prompt('Nombre para este perfil de impresión:', currentProfileName);
    if (!profileName) return;

    const newProfile: CalculationProfile = {
      id: `profile-${Date.now()}`,
      name: profileName,
      currency,
      kwhPrice,
      selectedPrinterId,
      powerWatts,
      lifespanHours,
      replacementCost,
      errorMarginPercent,
      filaments,
      printHours,
      printMinutes,
      suppliesCost,
      designMode,
      designFixedCost,
      designHourlyRate,
      designHours,
      postProcessMode,
      postProcessFixedCost,
      postProcessHourlyRate,
      postProcessHours,
      profitMode,
      profitMultiplier,
      profitPercentage,
      externalCommissionPercent,
      createdAt: new Date().toISOString(),
    };

    const updated = [...savedProfiles, newProfile];
    setSavedProfiles(updated);
    setCurrentProfileName(profileName);
    setSelectedSavedProfileId(newProfile.id);
    localStorage.setItem(STORAGE_PROFILES_KEY, JSON.stringify(updated));
    toast.success(`Perfil "${profileName}" guardado.`);
  };

  const handleOverwriteProfile = () => {
    if (!selectedSavedProfileId) {
      toast.error('Selecciona primero un perfil para sobrescribir o haz clic en "Guardar perfil".');
      return;
    }

    const targetProfile = savedProfiles.find((p) => p.id === selectedSavedProfileId);
    if (!targetProfile) return;

    const updatedProfile: CalculationProfile = {
      ...targetProfile,
      currency,
      kwhPrice,
      selectedPrinterId,
      powerWatts,
      lifespanHours,
      replacementCost,
      errorMarginPercent,
      filaments,
      printHours,
      printMinutes,
      suppliesCost,
      designMode,
      designFixedCost,
      designHourlyRate,
      designHours,
      postProcessMode,
      postProcessFixedCost,
      postProcessHourlyRate,
      postProcessHours,
      profitMode,
      profitMultiplier,
      profitPercentage,
      externalCommissionPercent,
    };

    const updated = savedProfiles.map((p) => (p.id === selectedSavedProfileId ? updatedProfile : p));
    setSavedProfiles(updated);
    localStorage.setItem(STORAGE_PROFILES_KEY, JSON.stringify(updated));
    toast.success(`Perfil "${targetProfile.name}" actualizado.`);
  };

  const handleLoadProfile = (profileId: string) => {
    const p = savedProfiles.find((item) => item.id === profileId);
    if (!p) return;

    setSelectedSavedProfileId(p.id);
    setCurrentProfileName(p.name);
    setCurrency(p.currency);
    setKwhPrice(p.kwhPrice);
    setSelectedPrinterId(p.selectedPrinterId);
    setPowerWatts(p.powerWatts);
    setLifespanHours(p.lifespanHours);
    setReplacementCost(p.replacementCost);
    setErrorMarginPercent(p.errorMarginPercent);
    setFilaments(p.filaments);
    setPrintHours(p.printHours);
    setPrintMinutes(p.printMinutes);
    setSuppliesCost(p.suppliesCost);
    setDesignMode(p.designMode);
    setDesignFixedCost(p.designFixedCost);
    setDesignHourlyRate(p.designHourlyRate);
    setDesignHours(p.designHours);
    setPostProcessMode(p.postProcessMode);
    setPostProcessFixedCost(p.postProcessFixedCost);
    setPostProcessHourlyRate(p.postProcessHourlyRate);
    setPostProcessHours(p.postProcessHours);
    setProfitMode(p.profitMode);
    setProfitMultiplier(p.profitMultiplier);
    setProfitPercentage(p.profitPercentage);
    setExternalCommissionPercent(p.externalCommissionPercent);
    toast.success(`Perfil "${p.name}" cargado.`);
  };

  const handleApplyWeightToFilament = (grams: number) => {
    if (filaments.length > 0) {
      handleUpdateFilament(filaments[0].id, { weightGrams: grams });
    }
  };

  // Currency label & symbol
  const currentCurrency = CURRENCIES.find((c) => c.code === currency) || CURRENCIES[0];

  const formatCurrency = (amount: number) => {
    return `${currentCurrency.symbol} ${amount.toLocaleString('es-AR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  return (
    <div className="space-y-8 max-w-7xl mx-auto pb-16">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 border-b border-border/80 pb-5">
        <div>
          <div className="flex items-center gap-2.5 mb-1">
            <div className="p-2 bg-primary/10 text-primary rounded-xl">
              <Calculator className="w-6 h-6" />
            </div>
            <h1 className="text-3xl font-extrabold tracking-tight text-foreground">
              Calculadora de Costos
            </h1>
          </div>
          <p className="text-sm text-muted-foreground">
            Calcula el precio justo para tus impresiones 3D
          </p>
        </div>

        {/* Currency Switcher */}
        <div className="flex items-center gap-1.5 bg-muted/60 p-1.5 rounded-2xl border border-border">
          {CURRENCIES.map((curr) => (
            <button
              key={curr.code}
              type="button"
              onClick={() => handleCurrencyChange(curr.code)}
              className={`px-3.5 py-1.5 rounded-xl text-xs font-bold transition-all ${
                currency === curr.code
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground hover:bg-background/60'
              }`}
            >
              {curr.symbol}
            </button>
          ))}
        </div>
      </div>

      {/* Profiles Bar */}
      <Card className="border border-border/80 bg-card/60 shadow-xs">
        <CardContent className="p-4 flex flex-col md:flex-row md:items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
            <Sliders className="w-4 h-4 text-primary" />
            <span>Perfiles de Impresión:</span>
            <span className="text-xs font-normal text-muted-foreground bg-accent/60 px-2.5 py-0.5 rounded-full">
              {currentProfileName}
            </span>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {savedProfiles.length > 0 && (
              <select
                value={selectedSavedProfileId}
                onChange={(e) => handleLoadProfile(e.target.value)}
                className="h-9 px-3 rounded-xl border border-input bg-card text-xs font-medium focus:ring-1 focus:ring-primary outline-none"
              >
                <option value="">Cargar perfil...</option>
                {savedProfiles.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            )}

            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleOverwriteProfile}
              disabled={!selectedSavedProfileId}
              className="gap-1.5 text-xs font-medium"
            >
              <RotateCcw className="w-3.5 h-3.5 text-muted-foreground" />
              Sobrescribir existente
            </Button>

            <Button
              type="button"
              variant="default"
              size="sm"
              onClick={handleSaveProfile}
              className="gap-1.5 text-xs font-semibold shadow-xs"
            >
              <Save className="w-3.5 h-3.5" />
              Guardar perfil
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Main Grid: Left inputs & 3D tabs, Right sticky summary */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        {/* Left Column (Inputs & 3D Tools) */}
        <div className="lg:col-span-8 space-y-8">
          {/* Section: 3D Model Analyzer & 3D Viewer */}
          <Card className="border border-border/80 bg-card/90 shadow-sm overflow-hidden">
            <div className="border-b border-border/80 bg-muted/30 px-6 py-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setActive3DTab('analyzer')}
                  className={`px-3 py-1.5 text-xs font-bold rounded-xl transition-all ${
                    active3DTab === 'analyzer'
                      ? 'bg-primary text-primary-foreground shadow-xs'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Analizador de modelo 3D
                </button>
                <button
                  type="button"
                  onClick={() => setActive3DTab('viewer')}
                  className={`px-3 py-1.5 text-xs font-bold rounded-xl transition-all ${
                    active3DTab === 'viewer'
                      ? 'bg-primary text-primary-foreground shadow-xs'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Visor 3D {loadedObject3D && '●'}
                </button>
              </div>

              {modelStats && (
                <span className="text-xs text-muted-foreground font-mono">
                  {modelStats.dimensions.x}×{modelStats.dimensions.y}×{modelStats.dimensions.z}mm
                </span>
              )}
            </div>

            <CardContent className="p-6">
              {active3DTab === 'analyzer' ? (
                <Model3DAnalyzer
                  currentStats={modelStats}
                  onModelLoaded={(obj, stats) => {
                    setLoadedObject3D(obj);
                    setModelStats(stats);
                  }}
                  onClearModel={() => {
                    setLoadedObject3D(null);
                    setModelStats(null);
                  }}
                  onApplyWeightToFilament={handleApplyWeightToFilament}
                  onGoToViewer={() => setActive3DTab('viewer')}
                />
              ) : (
                <Model3DViewer
                  object3D={loadedObject3D}
                  stats={modelStats}
                  filamentColorHex={filaments[0]?.colorHex || '#c2410c'}
                  onGoToAnalyzer={() => setActive3DTab('analyzer')}
                />
              )}
            </CardContent>
          </Card>

          {/* Section: Gastos Fijos */}
          <Card className="border border-border/80 bg-card shadow-sm">
            <CardHeader className="pb-4">
              <div className="flex items-center gap-2 text-foreground">
                <Zap className="w-5 h-5 text-primary" />
                <CardTitle className="text-lg font-bold">Gastos Fijos</CardTitle>
              </div>
              <CardDescription>
                Parámetros de electricidad, consumo y amortización de la impresora.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs font-semibold">Precio Kwh ({currentCurrency.symbol})</Label>
                  <div className="relative">
                    <Input
                      type="number"
                      step="any"
                      value={kwhPrice}
                      onChange={(e) => setKwhPrice(parseFloat(e.target.value) || 0)}
                      placeholder="120"
                      className="pr-12 font-mono"
                    />
                    <span className="absolute right-3 top-2.5 text-xs text-muted-foreground font-medium">
                      /kWh
                    </span>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs font-semibold">Perfiles de impresora:</Label>
                  <select
                    value={selectedPrinterId}
                    onChange={(e) => handlePrinterChange(e.target.value)}
                    className="w-full h-10 px-3 rounded-lg border border-input bg-card text-xs font-medium focus:ring-1 focus:ring-primary outline-none"
                  >
                    <option value="">Seleccionar impresora...</option>
                    {PRINTER_PRESETS.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name} ({p.powerWatts}W)
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 pt-2">
                <div className="space-y-2">
                  <Label className="text-xs font-semibold">Consumo real por hora (W)</Label>
                  <Input
                    type="number"
                    value={powerWatts}
                    onChange={(e) => setPowerWatts(parseFloat(e.target.value) || 0)}
                    placeholder="250"
                    className="font-mono"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-xs font-semibold">Desgaste máquina (horas)</Label>
                  <Input
                    type="number"
                    value={lifespanHours}
                    onChange={(e) => setLifespanHours(parseFloat(e.target.value) || 0)}
                    placeholder="3000"
                    className="font-mono"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-xs font-semibold">Precio Repuestos ({currentCurrency.symbol})</Label>
                  <Input
                    type="number"
                    value={replacementCost}
                    onChange={(e) => setReplacementCost(parseFloat(e.target.value) || 0)}
                    placeholder="40000"
                    className="font-mono"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-xs font-semibold">% Margen de Error</Label>
                  <div className="relative">
                    <Input
                      type="number"
                      value={errorMarginPercent}
                      onChange={(e) => setErrorMarginPercent(parseFloat(e.target.value) || 0)}
                      placeholder="8"
                      className="pr-8 font-mono"
                    />
                    <span className="absolute right-3 top-2.5 text-xs text-muted-foreground font-medium">
                      %
                    </span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Section: Pieza (Filamentos & Tiempo) */}
          <Card className="border border-border/80 bg-card shadow-sm">
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-foreground">
                  <Package className="w-5 h-5 text-primary" />
                  <CardTitle className="text-lg font-bold">Pieza</CardTitle>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleAddFilament}
                  className="gap-1.5 text-xs font-semibold"
                >
                  <Plus className="w-3.5 h-3.5" />
                  Agregar filamento
                </Button>
              </div>
              <CardDescription>
                Filamentos utilizados para la impresión y duración estimada del trabajo.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Filaments List */}
              <div className="space-y-4">
                {filaments.map((f, idx) => (
                  <div
                    key={f.id}
                    className="p-4 rounded-xl border border-border/80 bg-accent/20 space-y-3 relative group"
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                      <div className="flex items-center gap-2.5">
                        <div
                          className="w-5 h-5 rounded-full border shadow-xs shrink-0"
                          style={{ backgroundColor: f.colorHex }}
                        />
                        <span className="font-bold text-sm text-foreground">
                          {f.name || `Filamento ${idx + 1}`}
                        </span>
                      </div>

                      <div className="flex items-center gap-2">
                        {/* Selector de filamento guardado */}
                        <select
                          onChange={(e) => handleSelectSavedFilament(f.id, parseInt(e.target.value))}
                          defaultValue=""
                          className="h-8 px-2.5 rounded-lg border border-input bg-card text-xs font-medium focus:ring-1 focus:ring-primary outline-none"
                        >
                          <option value="" disabled>
                            Usar filamento guardado...
                          </option>
                          {SAVED_FILAMENTS.map((sf, sIdx) => (
                            <option key={sIdx} value={sIdx}>
                              {sf.name} - ${sf.pricePerKg}/kg
                            </option>
                          ))}
                        </select>

                        {filaments.length > 1 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveFilament(f.id)}
                            className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
                            title="Eliminar filamento"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-1">
                      <div className="space-y-1.5">
                        <Label className="text-xs font-semibold">
                          Precio/KG ({currentCurrency.symbol})
                        </Label>
                        <Input
                          type="number"
                          value={f.pricePerKg}
                          onChange={(e) =>
                            handleUpdateFilament(f.id, {
                              pricePerKg: parseFloat(e.target.value) || 0,
                            })
                          }
                          placeholder="19500"
                          className="font-mono"
                        />
                      </div>

                      <div className="space-y-1.5">
                        <div className="flex justify-between items-center">
                          <Label className="text-xs font-semibold">Gramos</Label>
                          {f.weightGrams > 0 && (
                            <span className="text-[11px] text-muted-foreground font-mono">
                              Costo: {formatCurrency((f.pricePerKg / 1000) * f.weightGrams)}
                            </span>
                          )}
                        </div>
                        <Input
                          type="number"
                          value={f.weightGrams}
                          onChange={(e) =>
                            handleUpdateFilament(f.id, {
                              weightGrams: parseFloat(e.target.value) || 0,
                            })
                          }
                          placeholder="65"
                          className="font-mono"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Tiempo de Impresión */}
              <div className="p-4 rounded-xl border border-border/80 bg-background/60 space-y-3">
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-primary" />
                  <Label className="text-sm font-bold text-foreground">Tiempo de impresión</Label>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground">Horas</Label>
                    <Input
                      type="number"
                      min="0"
                      value={printHours}
                      onChange={(e) => setPrintHours(parseInt(e.target.value) || 0)}
                      placeholder="4"
                      className="font-mono"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground">Minutos</Label>
                    <Input
                      type="number"
                      min="0"
                      max="59"
                      value={printMinutes}
                      onChange={(e) => setPrintMinutes(parseInt(e.target.value) || 0)}
                      placeholder="30"
                      className="font-mono"
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Section: Extras (Insumos, Diseño y Posprocesado) */}
          <Card className="border border-border/80 bg-card shadow-sm">
            <CardHeader className="pb-4">
              <div className="flex items-center gap-2 text-foreground">
                <Wrench className="w-5 h-5 text-primary" />
                <CardTitle className="text-lg font-bold">Extras</CardTitle>
              </div>
              <CardDescription>
                Insumos adicionales, modelado 3D y trabajo manual de posprocesado.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Insumos */}
              <div className="space-y-2">
                <Label className="text-xs font-semibold">
                  Insumos (Monto fijo: imanes, tornillos, packaging)
                </Label>
                <div className="relative">
                  <Input
                    type="number"
                    value={suppliesCost}
                    onChange={(e) => setSuppliesCost(parseFloat(e.target.value) || 0)}
                    placeholder="0"
                    className="font-mono"
                  />
                  <span className="absolute right-3 top-2.5 text-xs text-muted-foreground font-medium">
                    {currentCurrency.symbol}
                  </span>
                </div>
              </div>

              {/* Diseño y Modelado 3D */}
              <div className="p-4 rounded-xl border border-border/80 bg-accent/20 space-y-4">
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-bold text-foreground flex items-center gap-1.5">
                    <Brush className="w-3.5 h-3.5 text-primary" />
                    Diseño y Modelado 3D
                  </Label>
                  <div className="flex items-center gap-1 bg-background p-1 rounded-lg border border-border">
                    <button
                      type="button"
                      onClick={() => setDesignMode('fixed')}
                      className={`px-2.5 py-1 text-xs font-semibold rounded-md transition-all ${
                        designMode === 'fixed'
                          ? 'bg-primary text-primary-foreground'
                          : 'text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      Monto fijo
                    </button>
                    <button
                      type="button"
                      onClick={() => setDesignMode('hourly')}
                      className={`px-2.5 py-1 text-xs font-semibold rounded-md transition-all ${
                        designMode === 'hourly'
                          ? 'bg-primary text-primary-foreground'
                          : 'text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      Por hora
                    </button>
                  </div>
                </div>

                {designMode === 'fixed' ? (
                  <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground">Monto fijo ({currentCurrency.symbol})</Label>
                    <Input
                      type="number"
                      value={designFixedCost}
                      onChange={(e) => setDesignFixedCost(parseFloat(e.target.value) || 0)}
                      placeholder="0"
                      className="font-mono"
                    />
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label className="text-xs text-muted-foreground">Tarifa/hora ({currentCurrency.symbol})</Label>
                      <Input
                        type="number"
                        value={designHourlyRate}
                        onChange={(e) => setDesignHourlyRate(parseFloat(e.target.value) || 0)}
                        placeholder="8500"
                        className="font-mono"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs text-muted-foreground">Horas dedicadas</Label>
                      <Input
                        type="number"
                        step="0.25"
                        value={designHours}
                        onChange={(e) => setDesignHours(parseFloat(e.target.value) || 0)}
                        placeholder="1.5"
                        className="font-mono"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Posprocesado */}
              <div className="p-4 rounded-xl border border-border/80 bg-accent/20 space-y-4">
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-bold text-foreground flex items-center gap-1.5">
                    <Paintbrush className="w-3.5 h-3.5 text-primary" />
                    Posprocesado (lijado, pintura, ensamble)
                  </Label>
                  <div className="flex items-center gap-1 bg-background p-1 rounded-lg border border-border">
                    <button
                      type="button"
                      onClick={() => setPostProcessMode('fixed')}
                      className={`px-2.5 py-1 text-xs font-semibold rounded-md transition-all ${
                        postProcessMode === 'fixed'
                          ? 'bg-primary text-primary-foreground'
                          : 'text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      Monto fijo
                    </button>
                    <button
                      type="button"
                      onClick={() => setPostProcessMode('hourly')}
                      className={`px-2.5 py-1 text-xs font-semibold rounded-md transition-all ${
                        postProcessMode === 'hourly'
                          ? 'bg-primary text-primary-foreground'
                          : 'text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      Por hora
                    </button>
                  </div>
                </div>

                {postProcessMode === 'fixed' ? (
                  <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground">Monto fijo ({currentCurrency.symbol})</Label>
                    <Input
                      type="number"
                      value={postProcessFixedCost}
                      onChange={(e) => setPostProcessFixedCost(parseFloat(e.target.value) || 0)}
                      placeholder="0"
                      className="font-mono"
                    />
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label className="text-xs text-muted-foreground">Tarifa/hora ({currentCurrency.symbol})</Label>
                      <Input
                        type="number"
                        value={postProcessHourlyRate}
                        onChange={(e) => setPostProcessHourlyRate(parseFloat(e.target.value) || 0)}
                        placeholder="6000"
                        className="font-mono"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs text-muted-foreground">Horas dedicadas</Label>
                      <Input
                        type="number"
                        step="0.25"
                        value={postProcessHours}
                        onChange={(e) => setPostProcessHours(parseFloat(e.target.value) || 0)}
                        placeholder="0.5"
                        className="font-mono"
                      />
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Section: Ganancia */}
          <Card className="border border-border/80 bg-card shadow-sm">
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-foreground">
                  <Percent className="w-5 h-5 text-primary" />
                  <CardTitle className="text-lg font-bold">Ganancia</CardTitle>
                </div>
                <div className="flex items-center gap-1 bg-muted/60 p-1 rounded-xl border border-border">
                  <button
                    type="button"
                    onClick={() => setProfitMode('multiplier')}
                    className={`px-3 py-1 text-xs font-bold rounded-lg transition-all ${
                      profitMode === 'multiplier'
                        ? 'bg-primary text-primary-foreground shadow-xs'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    Multiplicador
                  </button>
                  <button
                    type="button"
                    onClick={() => setProfitMode('percentage')}
                    className={`px-3 py-1 text-xs font-bold rounded-lg transition-all ${
                      profitMode === 'percentage'
                        ? 'bg-primary text-primary-foreground shadow-xs'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    Porcentaje
                  </button>
                </div>
              </div>
              <CardDescription>
                Define tu margen de ganancia mediante factor multiplicador o porcentaje de recargo.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {profitMode === 'multiplier' ? (
                <div className="space-y-3">
                  <Label className="text-xs font-semibold">Margen de ganancia (multiplicador)</Label>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                    <button
                      type="button"
                      onClick={() => setProfitMultiplier(3)}
                      className={`p-3 rounded-xl border text-xs font-bold transition-all text-center ${
                        profitMultiplier === 3
                          ? 'border-primary bg-primary/10 text-primary ring-2 ring-primary/20'
                          : 'border-border bg-card hover:bg-accent'
                      }`}
                    >
                      Mayorista (x3)
                    </button>
                    <button
                      type="button"
                      onClick={() => setProfitMultiplier(4)}
                      className={`p-3 rounded-xl border text-xs font-bold transition-all text-center ${
                        profitMultiplier === 4
                          ? 'border-primary bg-primary/10 text-primary ring-2 ring-primary/20'
                          : 'border-border bg-card hover:bg-accent'
                      }`}
                    >
                      Minorista (x4)
                    </button>
                    <button
                      type="button"
                      onClick={() => setProfitMultiplier(5)}
                      className={`p-3 rounded-xl border text-xs font-bold transition-all text-center ${
                        profitMultiplier === 5
                          ? 'border-primary bg-primary/10 text-primary ring-2 ring-primary/20'
                          : 'border-border bg-card hover:bg-accent'
                      }`}
                    >
                      Llaveros (x5)
                    </button>
                    <div className="relative">
                      <Input
                        type="number"
                        step="0.1"
                        value={profitMultiplier}
                        onChange={(e) => setProfitMultiplier(parseFloat(e.target.value) || 1)}
                        placeholder="Personalizado"
                        className="text-center font-bold text-xs h-full min-h-[42px] border-primary/50"
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <Label className="text-xs font-semibold">Porcentaje de ganancia (%)</Label>
                  <div className="flex items-center gap-2">
                    {[50, 100, 150, 200].map((pct) => (
                      <button
                        key={pct}
                        type="button"
                        onClick={() => setProfitPercentage(pct)}
                        className={`flex-1 py-2 text-xs font-bold rounded-xl border transition-all ${
                          profitPercentage === pct
                            ? 'border-primary bg-primary/10 text-primary ring-2 ring-primary/20'
                            : 'border-border bg-card hover:bg-accent'
                        }`}
                      >
                        +{pct}%
                      </button>
                    ))}
                    <div className="relative w-28">
                      <Input
                        type="number"
                        value={profitPercentage}
                        onChange={(e) => setProfitPercentage(parseFloat(e.target.value) || 0)}
                        placeholder="%"
                        className="font-mono text-center pr-6"
                      />
                      <span className="absolute right-2.5 top-2.5 text-xs text-muted-foreground">%</span>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Section: Comisiones Externas */}
          <Card className="border border-border/80 bg-card shadow-sm">
            <CardHeader className="pb-4">
              <div className="flex items-center gap-2 text-foreground">
                <Share2 className="w-5 h-5 text-primary" />
                <CardTitle className="text-lg font-bold">Comisiones Externas</CardTitle>
              </div>
              <CardDescription>
                Comisión cobrada por pasarelas o marketplaces (Mercado Libre, Tienda Nube, Stripe, etc.).
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-w-xs">
                <Label className="text-xs font-semibold">Comisión externa (%)</Label>
                <div className="relative">
                  <Input
                    type="number"
                    value={externalCommissionPercent}
                    onChange={(e) => setExternalCommissionPercent(parseFloat(e.target.value) || 0)}
                    placeholder="18"
                    className="pr-8 font-mono"
                  />
                  <span className="absolute right-3 top-2.5 text-xs text-muted-foreground font-medium">
                    %
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Column: Live Sticky Summary Card */}
        <div className="lg:col-span-4 sticky top-6 space-y-6">
          <Card className="border-2 border-primary/25 bg-card/95 shadow-lg backdrop-blur-sm overflow-hidden">
            <CardHeader className="bg-primary/10 border-b border-primary/20 pb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-primary" />
                  <CardTitle className="text-lg font-bold text-foreground">
                    Desglose de Costos
                  </CardTitle>
                </div>
                <Badge variant="outline" className="border-primary/30 text-primary font-bold">
                  {currentCurrency.code}
                </Badge>
              </div>
            </CardHeader>

            <CardContent className="p-6 space-y-6">
              {/* Costos Base */}
              <div className="space-y-3">
                <h4 className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                  Costos Base
                </h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">Material</span>
                    <span className="font-mono font-semibold text-foreground">
                      {formatCurrency(calculations.materialCost)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">Electricidad</span>
                    <span className="font-mono font-semibold text-foreground">
                      {formatCurrency(calculations.electricityCost)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">Desgaste</span>
                    <span className="font-mono font-semibold text-foreground">
                      {formatCurrency(calculations.wearCost)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Costos Adicionales */}
              <div className="space-y-3 pt-3 border-t border-border/80">
                <h4 className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                  Costos Adicionales
                </h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">Margen Error ({errorMarginPercent}%)</span>
                    <span className="font-mono font-semibold text-foreground">
                      {formatCurrency(calculations.errorMarginCost)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground font-medium">Costo Producción</span>
                    <span className="font-mono font-semibold text-foreground">
                      {formatCurrency(calculations.productionCost)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">Insumos (fijo)</span>
                    <span className="font-mono font-semibold text-foreground">
                      {formatCurrency(calculations.supplies)}
                    </span>
                  </div>
                  {(calculations.designCost > 0 || calculations.postProcessCost > 0) && (
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">Extras (Diseño/Post)</span>
                      <span className="font-mono font-semibold text-foreground">
                        {formatCurrency(calculations.designCost + calculations.postProcessCost)}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between items-center pt-2 border-t border-dashed border-border/80 font-bold">
                    <span className="text-foreground">Total Costos</span>
                    <span className="font-mono text-primary">
                      {formatCurrency(calculations.totalCosts)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Precio Final (Highlight Box) */}
              <div className="p-4 rounded-2xl bg-primary/10 border-2 border-primary/20 space-y-1">
                <p className="text-xs font-bold uppercase tracking-wider text-primary">
                  Precio Final
                </p>
                <div className="flex flex-col">
                  <span className="text-xs font-semibold text-muted-foreground">
                    TOTAL A COBRAR ({profitMode === 'multiplier' ? `x${profitMultiplier}` : `+${profitPercentage}%`})
                  </span>
                  <span className="text-3xl font-black tracking-tight text-primary font-mono mt-1">
                    {formatCurrency(calculations.totalPrice)}
                  </span>
                </div>
              </div>

              {/* Comisiones Externas Box */}
              {calculations.commissionPercent > 0 && (
                <div className="p-4 rounded-xl bg-accent/30 border border-border/80 space-y-1">
                  <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                    Comisiones Externas
                  </p>
                  <div className="flex justify-between items-baseline pt-1">
                    <span className="text-xs text-muted-foreground">
                      Precio con comisión (+{calculations.commissionPercent}%)
                    </span>
                    <span className="font-bold text-foreground font-mono text-base">
                      {formatCurrency(calculations.priceWithCommission)}
                    </span>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="space-y-2.5 pt-2">
                <Button
                  type="button"
                  onClick={() => setIsQuoteModalOpen(true)}
                  className="w-full gap-2 py-6 font-bold text-sm shadow-md"
                >
                  <Sparkles className="w-4 h-4" />
                  Generar Presupuesto / WhatsApp
                </Button>

                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    const text = `*PRESUPUESTO 3D* 🖨️
Pieza: ${modelStats?.fileName || 'Impresión 3D'}
Peso: ${calculations.totalGrams}g | Tiempo: ${printHours}h ${printMinutes}m
*TOTAL A COBRAR:* ${formatCurrency(calculations.totalPrice)}
*Con plataforma (+${calculations.commissionPercent}%):* ${formatCurrency(calculations.priceWithCommission)}`;
                    navigator.clipboard.writeText(text);
                    toast.success('¡Resumen rápido copiado al portapapeles!');
                  }}
                  className="w-full gap-2 text-xs font-semibold"
                >
                  <Copy className="w-3.5 h-3.5 text-muted-foreground" />
                  Copiar cotización rápida
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Quote / Print Dialog Modal */}
      <PrintSummaryModal
        isOpen={isQuoteModalOpen}
        onClose={() => setIsQuoteModalOpen(false)}
        currencySymbol={currentCurrency.symbol}
        currencyCode={currency}
        modelName={modelStats?.fileName || 'Pieza 3D Personalizada'}
        printHours={printHours}
        printMinutes={printMinutes}
        totalGrams={calculations.totalGrams}
        materialNames={filaments.map((f) => f.name).join(', ')}
        totalCost={calculations.totalCosts}
        totalPrice={calculations.totalPrice}
        priceWithCommission={calculations.priceWithCommission}
        commissionPercent={calculations.commissionPercent}
      />
    </div>
  );
}
